import type { Insights, TrendInsight } from '../lib/api';

/**
 * AI Insights panel (§ AI Prompt Reporting — "AI Instantly Delivers"). Renders the
 * server-generated deliverables over a result: an executive summary, KPI callouts,
 * a trend read, and recommended actions. `source` distinguishes an AI narrative from
 * the deterministic fallback so the user always knows which they're seeing.
 */
export function InsightsPanel({ insights }: { insights: Insights }): React.JSX.Element | null {
  const { executiveSummary, kpis, trend, rootCause, recommendations, source } = insights;
  if (!executiveSummary && kpis.length === 0 && !trend && !rootCause && recommendations.length === 0) return null;

  return (
    <section style={panelStyle} aria-label="AI insights">
      <div style={headerRow}>
        <span style={heading}>AI Insights</span>
        <span style={source === 'llm' ? badgeAi : badgeComputed}>{source === 'llm' ? 'AI-generated' : 'computed'}</span>
      </div>

      {executiveSummary ? <p style={summaryStyle}>{executiveSummary}</p> : null}

      {kpis.length > 0 ? (
        <div style={kpiRow}>
          {kpis.map((k, i) => (
            <div key={`${k.label}-${i}`} style={kpiCard}>
              <div style={kpiLabel}>{k.label}</div>
              <div style={kpiValueRow}>
                <span style={kpiValue}>{k.value}</span>
                {k.delta ? <span style={deltaStyle(k.delta)}>{k.delta}</span> : null}
              </div>
              {k.note ? <div style={kpiNote}>{k.note}</div> : null}
            </div>
          ))}
        </div>
      ) : null}

      {trend ? (
        <div style={trendRow}>
          <span style={trendBadge(trend.direction)}>
            {arrow(trend.direction)}
            {trend.changePct != null ? ` ${trend.changePct > 0 ? '+' : ''}${trend.changePct}%` : ''}
          </span>
          <span style={trendText}>{trend.narrative}</span>
        </div>
      ) : null}

      {rootCause ? (
        <div style={{ marginTop: 14 }}>
          <div style={recHeading}>Root cause</div>
          <p style={{ ...trendText, margin: '0 0 8px' }}>{rootCause.narrative}</p>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {rootCause.drivers.map((d, i) => (
              <div key={`${d.label}-${i}`} style={driverRow}>
                <span style={driverLabel} title={d.label}>
                  {d.label}
                </span>
                <span style={driverBarTrack}>
                  <span style={{ ...driverBarFill, width: `${Math.max(2, Math.min(100, d.share))}%` }} />
                </span>
                <span style={driverShare}>{d.share}%</span>
                <span style={driverValue}>{d.value}</span>
              </div>
            ))}
          </div>
        </div>
      ) : null}

      {recommendations.length > 0 ? (
        <div style={{ marginTop: 14 }}>
          <div style={recHeading}>Recommended actions</div>
          <ul style={recList}>
            {recommendations.map((r, i) => (
              <li key={i} style={recItem}>
                {r}
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  );
}

function arrow(d: TrendInsight['direction']): string {
  return d === 'up' ? '▲' : d === 'down' ? '▼' : '▶';
}

const ACCENT = 'var(--accent, #4f46e5)';

const panelStyle: React.CSSProperties = {
  marginTop: 16,
  padding: 16,
  borderRadius: 12,
  border: '1px solid var(--border, rgba(120,120,140,0.25))',
  background: 'var(--panel, rgba(120,120,160,0.06))',
};
const headerRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 };
const heading: React.CSSProperties = { fontSize: 13, fontWeight: 700, letterSpacing: '0.04em', textTransform: 'uppercase', color: 'var(--text)' };
const badgeBase: React.CSSProperties = { fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 999, lineHeight: 1.6 };
const badgeAi: React.CSSProperties = { ...badgeBase, color: ACCENT, background: 'color-mix(in srgb, var(--accent, #4f46e5) 14%, transparent)' };
const badgeComputed: React.CSSProperties = { ...badgeBase, color: 'var(--text-muted)', background: 'var(--plane, rgba(120,120,140,0.12))' };

const summaryStyle: React.CSSProperties = { margin: '0 0 12px', color: 'var(--text-secondary)', fontSize: 14.5, lineHeight: 1.55 };

const kpiRow: React.CSSProperties = { display: 'flex', flexWrap: 'wrap', gap: 10, marginBottom: 4 };
const kpiCard: React.CSSProperties = {
  flex: '1 1 140px',
  minWidth: 120,
  padding: '10px 12px',
  borderRadius: 10,
  background: 'var(--plane, rgba(120,120,140,0.10))',
};
const kpiLabel: React.CSSProperties = { fontSize: 12, color: 'var(--text-muted)', marginBottom: 4 };
const kpiValueRow: React.CSSProperties = { display: 'flex', alignItems: 'baseline', gap: 6 };
const kpiValue: React.CSSProperties = { fontSize: 22, fontWeight: 700, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' };
const kpiNote: React.CSSProperties = { fontSize: 11.5, color: 'var(--text-muted)', marginTop: 2 };
function deltaStyle(delta: string): React.CSSProperties {
  const positive = delta.trim().startsWith('+');
  const negative = delta.trim().startsWith('-');
  return { fontSize: 12.5, fontWeight: 600, color: positive ? '#16a34a' : negative ? '#dc2626' : 'var(--text-muted)' };
}

const trendRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, marginTop: 12 };
function trendBadge(d: TrendInsight['direction']): React.CSSProperties {
  const color = d === 'up' ? '#16a34a' : d === 'down' ? '#dc2626' : 'var(--text-muted)';
  return { fontSize: 12.5, fontWeight: 700, color, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' };
}
const trendText: React.CSSProperties = { fontSize: 13.5, color: 'var(--text-secondary)', lineHeight: 1.5 };

const driverRow: React.CSSProperties = { display: 'flex', alignItems: 'center', gap: 10, fontSize: 13 };
const driverLabel: React.CSSProperties = { flex: '0 0 130px', color: 'var(--text)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' };
const driverBarTrack: React.CSSProperties = { flex: 1, height: 8, borderRadius: 999, background: 'var(--plane, rgba(120,120,140,0.15))', overflow: 'hidden' };
const driverBarFill: React.CSSProperties = { display: 'block', height: '100%', borderRadius: 999, background: ACCENT };
const driverShare: React.CSSProperties = { flex: '0 0 42px', textAlign: 'right', fontWeight: 600, color: 'var(--text)', fontVariantNumeric: 'tabular-nums' };
const driverValue: React.CSSProperties = { flex: '0 0 60px', textAlign: 'right', color: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' };

const recHeading: React.CSSProperties = { fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.04em', marginBottom: 6 };
const recList: React.CSSProperties = { margin: 0, paddingLeft: 18, display: 'flex', flexDirection: 'column', gap: 4 };
const recItem: React.CSSProperties = { fontSize: 14, color: 'var(--text-secondary)', lineHeight: 1.5 };
